import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, Container, Divider, Grid, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { buildContractSearchParams } from '../newJourney/buildContractSearchParams';
import FileIcon from '~/assets/icons/File';
import FileImage from '~/assets/icons/FileImage';
import InputFile from '~/components/InputFile';
import SidebarMesages from '~/components/PersonalCoverage/SidebarMesages';
import { IFile } from '~/services/modules/contract/types';
import { storeState } from '~/store';
import { VoucherError } from './VoucherError';
import RemoteImageIcon from './components/RemoteImageIcon';
import { TypeFile, useEmailFlow, useRenewFlow, useStandardFlow } from './hooks';
import SuraNotNew from './partials/SuraNotNew';
import * as styles from './styles';

export default function FileComponent() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const cid = searchParams.get('cid');
  const source = searchParams.get('source');
  const intMod = searchParams.get('int_mod');
  const isEmailMode = source === 'email' && intMod === '1' && !!cid;
  const isRenewMode = source === 'renewal' && !!cid;

  if (!isEmailMode && !isRenewMode) {
    return <Navigate replace to={`/contract/equipment${buildContractSearchParams(searchParams)}`} />;
  }

  return <FileComponentEmailOrRenew cid={cid} isEmailMode={isEmailMode} isRenewMode={isRenewMode} />;
}

function FileComponentEmailOrRenew({
  cid,
  isEmailMode,
  isRenewMode
}: {
  cid: string | null;
  isEmailMode: boolean;
  isRenewMode: boolean;
}) {
  const emailFlow = useEmailFlow(isEmailMode ? cid : null);
  const standardFlow = useStandardFlow();
  const renewFlow = useRenewFlow(isRenewMode ? cid : null);

  // Escolher qual fluxo usar
  const {
    loading,
    dataLoaded,
    hasVoucherError,
    errors,
    hasInvoice,
    isNew,
    product,
    productId,
    voucher,
    documentUrls,
    isEmailMode: isInEmailMode,
    submitError,
    submitSuccess,
    onChangeFile,
    onSubmit
  } = isEmailMode ? emailFlow : isRenewMode ? renewFlow : standardFlow;

  // Gerenciar toasts baseado nos estados de erro e sucesso
  useEffect(() => {
    if (submitError) {
      storeState.addToast(submitError);
    }
  }, [submitError]);

  useEffect(() => {
    if (submitSuccess) {
      storeState.addToast(submitSuccess);
    }
  }, [submitSuccess]);

  const onChangeFileHandler = (key: TypeFile, value: IFile) => {
    onChangeFile(key, value);
  };

  const renderUploads = () => {
    if (hasInvoice && isNew) {
      return (
        <Grid item xs={12} sm={6} display={'flex'} alignItems={'flex-end'}>
          <InputFile
            error={errors?.invoice}
            label="Nota Fiscal"
            onChangeFile={(file) => onChangeFileHandler('invoice', file)}
            baseIcon={
              isEmailMode && isInEmailMode && documentUrls?.invoice ? (
                <RemoteImageIcon imageUrl={documentUrls.invoice} fallbackIcon={<FileIcon />} />
              ) : (
                <FileIcon />
              )
            }
            alternateIcon={<FileImage />}
          />
        </Grid>
      );
    }

    if (hasInvoice && !isNew && productId.includes(5)) {
      return (
        <SuraNotNew
          onChangeFile={onChangeFile}
          errors={errors}
          equipamentTypeId={product}
          isEmailMode={isEmailMode && isInEmailMode}
          documentUrls={documentUrls}
        />
      );
    }

    if (hasInvoice && !isNew) {
      return (
        <>
          <Grid item xs={12} sm={6} display={'flex'} alignItems={'flex-end'}>
            <InputFile
              error={errors?.invoice}
              label="Nota Fiscal"
              onChangeFile={(file) => onChangeFileHandler('invoice', file)}
              baseIcon={
                isEmailMode && isInEmailMode && documentUrls?.invoice ? (
                  <RemoteImageIcon imageUrl={documentUrls.invoice} fallbackIcon={<FileIcon />} />
                ) : (
                  <FileIcon />
                )
              }
              alternateIcon={<FileImage />}
            />
          </Grid>
          <Grid item xs={12} sm={6} display={'flex'} alignItems={'flex-end'}>
            <InputFile
              error={errors?.overview}
              label="Equipamento inteiro"
              baseIcon={
                isEmailMode && isInEmailMode && documentUrls?.overview ? (
                  <RemoteImageIcon imageUrl={documentUrls.overview} fallbackIcon={<FileIcon />} />
                ) : (
                  <FileIcon />
                )
              }
              alternateIcon={<FileImage />}
              onChangeFile={(file) => onChangeFileHandler('overview', file)}
            />
          </Grid>
          <Grid item xs={12} sm={6} display={'flex'} alignItems={'flex-end'}>
            <InputFile
              error={errors?.serial_number_photo}
              label="Número de série"
              onChangeFile={(file) => onChangeFileHandler('serial_number_photo', file)}
              baseIcon={
                isEmailMode && isInEmailMode && documentUrls?.serial_number_photo ? (
                  <RemoteImageIcon imageUrl={documentUrls.serial_number_photo} fallbackIcon={<FileIcon />} />
                ) : (
                  <FileIcon />
                )
              }
              alternateIcon={<FileImage />}
            />
          </Grid>
          <Grid item xs={12} sm={6} display={'flex'} alignItems={'flex-end'}>
            <InputFile
              error={errors?.ratchets_group}
              label="Grupo de catracas"
              onChangeFile={(file) => onChangeFileHandler('ratchets_group', file)}
              baseIcon={
                isEmailMode && isInEmailMode && documentUrls?.ratchets_group ? (
                  <RemoteImageIcon imageUrl={documentUrls.ratchets_group} fallbackIcon={<FileIcon />} />
                ) : (
                  <FileIcon />
                )
              }
              alternateIcon={<FileImage />}
            />
          </Grid>
        </>
      );
    }

    if (!hasInvoice) {
      return (
        <>
          <Grid item xs={12} sm={6} display={'flex'} alignItems={'flex-end'}>
            <InputFile
              error={errors?.overview}
              label="Equipamento inteiro"
              onChangeFile={(file) => onChangeFileHandler('overview', file)}
              baseIcon={
                isEmailMode && isInEmailMode && documentUrls?.overview ? (
                  <RemoteImageIcon imageUrl={documentUrls.overview} fallbackIcon={<FileIcon />} />
                ) : (
                  <FileIcon />
                )
              }
              alternateIcon={<FileImage />}
            />
          </Grid>
          <Grid item xs={12} sm={6} display={'flex'} alignItems={'flex-end'}>
            <InputFile
              error={errors?.serial_number_photo}
              label="Número de série"
              onChangeFile={(file) => onChangeFileHandler('serial_number_photo', file)}
              baseIcon={
                isEmailMode && isInEmailMode && documentUrls?.serial_number_photo ? (
                  <RemoteImageIcon imageUrl={documentUrls.serial_number_photo} fallbackIcon={<FileIcon />} />
                ) : (
                  <FileIcon />
                )
              }
              alternateIcon={<FileImage />}
            />
          </Grid>
          <Grid item xs={12} sm={6} display={'flex'} alignItems={'flex-end'}>
            <InputFile
              error={errors?.ratchets_group}
              label="Grupo de catracas"
              onChangeFile={(file) => onChangeFileHandler('ratchets_group', file)}
              baseIcon={
                isEmailMode && isInEmailMode && documentUrls?.ratchets_group ? (
                  <RemoteImageIcon imageUrl={documentUrls.ratchets_group} fallbackIcon={<FileIcon />} />
                ) : (
                  <FileIcon />
                )
              }
              alternateIcon={<FileImage />}
            />
          </Grid>
        </>
      );
    }
  };

  const renderContent = (hasInvoice: boolean) => {
    return (
      <Card>
        <CardContent>
          <Box sx={styles.headerContainer}>
            <Typography variant="h6" align="center" fontWeight="bold">
              {isRenewMode ? 'Renovação do Contrato' : 'Fotos para ' + (isEmailMode ? 'moderação' : 'contrato')}
            </Typography>
          </Box>
          <Divider variant="middle" />
          <Box sx={styles.containerForm}>
            <Typography sx={{ fontSize: { xs: 16, sm: 20 } }} variant="h6" fontWeight="bold">
              {isEmailMode
                ? 'Fotos do Equipamento'
                : isRenewMode
                ? 'Vamos iniciar o processo de renovação '
                : '4 - Fotos do Equipamento'}
            </Typography>
            {isRenewMode && (
              <Typography sx={{ fontSize: { xs: 16, sm: 18 } }} variant="body1" fontWeight="bold">
                Para renovar seu contrato precisamos que você envie as fotos atualizadas do seu equipamento
              </Typography>
            )}
            <Typography sx={styles.descritionTitle}>
              *Atenção! É muito importante observar a qualidade das fotografias e a nitidez dos documentos
              digitalizados. A baixa qualidade dos documentos anexados poderá implicar na recusa por parte da seguradora
              da aceitação do seu seguro. Não deixe que isso aconteça
            </Typography>
            {!hasInvoice && !isEmailMode && !isRenewMode && (
              <Typography>
                <Typography sx={styles.warningText} component={'span'}>
                  IMPORTANTE:{' '}
                </Typography>
                O voucher{' '}
                <Typography sx={styles.voucherStyle} component={'span'}>
                  {voucher}
                </Typography>{' '}
                é necessário enviar junto a foto da visão geral escrito em um papel fixado no equipamento.
              </Typography>
            )}
          </Box>
          <Grid container spacing={2}>
            {renderUploads()}
          </Grid>
          <Box sx={styles.containerButton}>
            {isEmailMode ? (
              <LoadingButton
                sx={{ fontSize: { xs: 16, sm: 20 } }}
                loading={loading}
                variant="contained"
                onClick={onSubmit}
              >
                Enviar fotos
              </LoadingButton>
            ) : isRenewMode ? (
              <LoadingButton
                sx={{ fontSize: { xs: 16, sm: 20 } }}
                loading={loading}
                variant="contained"
                onClick={onSubmit}
              >
                Enviar fotos para renovação
              </LoadingButton>
            ) : (
              <>
                <LoadingButton sx={{ fontSize: { xs: 16, sm: 20 } }} loading={loading} onClick={onSubmit}>
                  Salvar e sair
                </LoadingButton>
                <LoadingButton
                  data-testid="contract-step4-next"
                  sx={{ fontSize: { xs: 16, sm: 20 } }}
                  loading={loading}
                  variant="contained"
                  onClick={onSubmit}
                >
                  Próximo passo
                </LoadingButton>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // O efeito para buscar o voucher foi movido para dentro do useStandardFlow

  if (hasVoucherError) {
    return <VoucherError />;
  }

  // Se estiver no modo de email/renovação e os dados ainda não foram carregados, mostrar loading
  if ((isEmailMode || isRenewMode) && !dataLoaded) {
    return (
      <Container maxWidth="lg" sx={styles.container}>
        <Card>
          <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 4 }}>
            <Typography variant="h6">
              Carregando informações do {isRenewMode ? 'contrato de renovação' : 'contrato'}...
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Grid container spacing={2}>
        <Grid item md={isEmailMode || isRenewMode ? 12 : 8} xs={12}>
          {renderContent(hasInvoice ?? false)}
        </Grid>
        {!isEmailMode && !isRenewMode && (
          <Grid item md={4} xs={12}>
            <Card>
              <CardContent>
                <SidebarMesages showProduct={true} />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
